import { useState } from 'react';
import { flushSync } from 'react-dom';

import {
    autoPlacement,
    autoUpdate,
    offset,
    size,
    useClick,
    useDismiss,
    useFloating,
    useInteractions,
    useTransitionStyles,
} from '@floating-ui/react';
import type { Placement } from '@floating-ui/react';

type useFloatingDropdownConfig = {
    defaultOpen?: boolean;
    placement?: Placement;
    allowedPlacements?: Placement[];
    offsetPx?: number;
    onOpenChange?: (open: boolean) => void;
};

const DEFAULT_ALLOWED_PLACEMENTS: Placement[] = ['bottom-start', 'top-start', 'bottom-end', 'top-end'];

export function useFloatingDropdown({
    defaultOpen = false,
    placement = 'bottom-start',
    allowedPlacements = DEFAULT_ALLOWED_PLACEMENTS,
    offsetPx = 2,
    onOpenChange,
}: useFloatingDropdownConfig = {}) {
    const [isOpen, setIsOpen] = useState(defaultOpen);
    const [maxHeight, setMaxHeight] = useState<number | null>(null);
    const { refs, floatingStyles, context } = useFloating({
        middleware: [
            autoPlacement({
                allowedPlacements,
            }),
            offset(offsetPx),
            size({
                apply({ availableHeight }) {
                    flushSync(() => setMaxHeight(availableHeight - 20));
                },
            }),
        ],
        open: isOpen,
        onOpenChange: (open) => {
            setIsOpen(open);
            onOpenChange?.(open);
        },
        placement,
        whileElementsMounted: autoUpdate,
    });
    const click = useClick(context, { toggle: true });
    const dismiss = useDismiss(context);
    const { getReferenceProps, getFloatingProps } = useInteractions([click, dismiss]);
    const { styles, isMounted } = useTransitionStyles(context);

    return {
        isOpen,
        setIsOpen,
        refs,
        floatingStyles,
        context,
        getReferenceProps,
        getFloatingProps,
        styles,
        isMounted,
        maxHeight,
    };
}
